import { Coordinates, MapBounds } from '../types';

type ParsedSharedMap = {
  center: Coordinates;
  zoom: number;
  bounds?: MapBounds;
};

type ParsedSharedWaypoint = {
  waypointId: string;
  latitude: number;
  longitude: number;
  name: string;
};

export type InitialRouteState = {
  isPrivacyPath: boolean;
  isTermsPath: boolean;
  showAndroidGuide: boolean;
  collabInviteParam: string | null;
  collabTokenParam: string | null;
  forcedLanguageParam: 'no' | 'en' | 'de' | 'pl' | 'es' | 'fr' | null;
  pendingOpenSpotAutoFavoriteParam: boolean;
  parsedSharedMap: ParsedSharedMap | null;
  resolvedReviewId: string | null;
  resolvedSharedSpotId: string | null;
  resolvedOpenSpotId: string | null;
  resolvedSharedRouteId: string | null;
  resolvedRouteId: string | null;
  resolvedSeoCounty: string | null;
  resolvedSeoMunicipality: string | null;
  resolvedSharedWaypoint: ParsedSharedWaypoint | null;
};

const decodeMaybe = (value: string | undefined) => value ? decodeURIComponent(value) : null;

export const resolveInitialRouteState = (location: Location): InitialRouteState => {
  const sharedReviewPathMatch = location.pathname.match(/^\/share\/review\/([^/]+)\/?$/);
  const sharedSpotPathMatch = location.pathname.match(/^\/share\/spot\/([^/]+)\/?$/);
  const appShareReviewPathMatch = location.pathname.match(/^\/app\/share\/review\/([^/]+)\/?$/);
  const appReviewPathMatch = location.pathname.match(/^\/app\/review\/([^/]+)\/?$/);
  const appOpenReviewPathMatch = location.pathname.match(/^\/app\/open\/review\/([^/]+)\/?$/);
  const appViewReviewPathMatch = location.pathname.match(/^\/app\/view\/review\/([^/]+)\/?$/);
  const appSpotPathMatch = location.pathname.match(/^\/app\/spot\/([^/]+)\/?$/);
  const appViewSpotPathMatch = location.pathname.match(/^\/app\/view\/spot\/([^/]+)\/?$/);
  const appOpenSpotPathMatch = location.pathname.match(/^\/app\/open\/spot\/([^/]+)\/?$/);
  const appMapPathMatch = location.pathname.match(/^\/app\/map\/([^/]+)\/?$/);
  const appRoutePathMatch = location.pathname.match(/^\/app\/route\/([^/]+)\/?$/);
  const appViewRoutePathMatch = location.pathname.match(/^\/app\/view\/route\/([^/]+)\/?$/);
  const appOpenRoutePathMatch = location.pathname.match(/^\/app\/open\/route\/([^/]+)\/?$/);
  const sharedWaypointPathMatch = location.pathname.match(/^\/share\/waypoint\/([^/]+)\/?$/);
  const sharedRoutePathMatch = location.pathname.match(/^\/share\/route\/([^/]+)\/?$/);
  const appShareRoutePathMatch = location.pathname.match(/^\/app\/share\/route\/([^/]+)\/?$/);
  const rutePathMatch = location.pathname.match(/^\/rute\/([^/]+)\/?$/);
  const appSeoMunicipalityPathMatch = location.pathname.match(/^\/app\/fricamping\/([^/]+)\/([^/]+)\/?$/);
  const appSeoCountyPathMatch = location.pathname.match(/^\/app\/fricamping\/([^/]+)\/?$/);
  const searchParams = new URLSearchParams(location.search);

  const sharedMapParam = searchParams.get('m') || decodeMaybe(appMapPathMatch?.[1]);
  const rawLanguage = (searchParams.get('lang') || searchParams.get('lng') || '').trim().toLowerCase();
  const rawFavorite = (searchParams.get('favorite') || searchParams.get('fav') || '').trim().toLowerCase();

  const parsedSharedMap = (() => {
    if (!sharedMapParam) return null;
    const parts = sharedMapParam.split(',').map((x) => Number(x));
    if (parts.length < 3 || !parts.every((n) => Number.isFinite(n))) return null;
    const [lat, lon, rawZoom] = parts;
    const zoom = Math.max(3, Math.min(18, Math.round(rawZoom)));
    const center = { latitude: lat, longitude: lon };
    if (parts.length >= 7) {
      const [latMin, lonMin, latMax, lonMax] = [parts[3], parts[4], parts[5], parts[6]];
      if ([latMin, lonMin, latMax, lonMax].every((n) => Number.isFinite(n))) {
        return { center, zoom, bounds: { latMin, lonMin, latMax, lonMax } };
      }
    }
    return { center, zoom };
  })();

  const resolvedSharedRouteId = (() => {
    let raw = sharedRoutePathMatch?.[1] || appShareRoutePathMatch?.[1] || appViewRoutePathMatch?.[1] || rutePathMatch?.[1] || null;
    if (rutePathMatch?.[1]) {
      const parts = rutePathMatch[1].split('-');
      if (parts.length > 1) raw = parts[parts.length - 1] || raw;
    }
    return raw;
  })();

  const forcedLanguageParam = (() => {
    if (rawLanguage.startsWith('en')) return 'en';
    if (rawLanguage.startsWith('de')) return 'de';
    if (rawLanguage.startsWith('pl')) return 'pl';
    if (rawLanguage.startsWith('es')) return 'es';
    if (rawLanguage.startsWith('fr')) return 'fr';
    if (rawLanguage.startsWith('no') || rawLanguage.startsWith('nb') || rawLanguage.startsWith('nn')) return 'no';
    return null;
  })();

  return {
    isPrivacyPath: location.pathname === '/privacy' || location.pathname === '/personvern',
    isTermsPath: location.pathname === '/vilkar' || location.pathname === '/terms',
    showAndroidGuide: searchParams.get('android') === '1',
    collabInviteParam: searchParams.get('collab'),
    collabTokenParam: searchParams.get('token'),
    forcedLanguageParam,
    pendingOpenSpotAutoFavoriteParam: rawFavorite === '1' || rawFavorite === 'true' || rawFavorite === 'yes',
    parsedSharedMap,
    resolvedReviewId: sharedReviewPathMatch?.[1] || appShareReviewPathMatch?.[1] || appReviewPathMatch?.[1] || appOpenReviewPathMatch?.[1] || appViewReviewPathMatch?.[1] || null,
    resolvedSharedSpotId: sharedSpotPathMatch?.[1] || appSpotPathMatch?.[1] || appViewSpotPathMatch?.[1] || null,
    resolvedOpenSpotId: decodeMaybe(appOpenSpotPathMatch?.[1]),
    resolvedSharedRouteId,
    resolvedRouteId: decodeMaybe(appRoutePathMatch?.[1] || appOpenRoutePathMatch?.[1]),
    resolvedSeoCounty: appSeoMunicipalityPathMatch?.[1] || appSeoCountyPathMatch?.[1] || null,
    resolvedSeoMunicipality: appSeoMunicipalityPathMatch?.[2] || null,
    resolvedSharedWaypoint: (() => {
      if (!sharedWaypointPathMatch?.[1]) return null;
      const lat = parseFloat(searchParams.get('lat') || '');
      const lon = parseFloat(searchParams.get('lon') || '');
      const name = searchParams.get('name') || 'Delt waypoint';
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return { waypointId: sharedWaypointPathMatch[1], latitude: lat, longitude: lon, name };
    })(),
  };
};
